import { useEffect, useRef, useState } from 'react'
import { Input, Modal, Select, Toggle } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { toast } from 'react-toastify'
import { IconPalette, IconLayout, IconColumns, IconRestore, IconSettings, IconTypography } from '@tabler/icons-react'
import { ChromePicker } from 'react-color'
import { useClient } from '@/stores/useClient.ts'
import { ProfProject } from '@/models/profProject.ts'
import {
  ThemeDefinitions,
  ThemeDefinitionsByName,
  DriveThemeStyleDefinition,
} from '@/models/drive.ts'
import { LayoutMode } from '@/components/Files/FilesListing.tsx'
import { allFonts, FontDefinition, getFontDefinitionById } from '@/models/fontDetails.ts'
import TutorialSpot from '@/components/tutorial/TutorialSpot.tsx'
import InspectorPreviewLayout from '@/components/portal/InspectorPreviewLayout.tsx'
import { useTutorial } from '@/contexts/tutorialContext.tsx'

interface ProjectDesignTabProps {
  project: ProfProject
  onUpdate: () => void
}

const SPACING_OPTIONS = [
  { value: '0', label: 'Kein Rand' },
  { value: '5', label: 'Klein' },
  { value: '10', label: 'Mittel' },
  { value: '15', label: 'Groß' },
  { value: '20', label: 'Sehr groß' },
  { value: 'custom', label: 'Eigener Wert' },
]

const LAYOUT_MODE_OPTIONS: { value: LayoutMode | ''; label: string }[] = [
  { value: 'grid', label: 'Kacheln' },
  { value: 'rows', label: 'Zeilen' },
  { value: 'columns', label: 'Spalten' },
  { value: 'masonry', label: 'Masonry' },
]

const ALBUM_OVERVIEW_OPTIONS: { value: string; label: string }[] = [
  { value: 'both', label: 'Desktop & Mobile' },
  { value: 'desktop', label: 'Nur Desktop' },
  { value: 'mobile', label: 'Nur Mobile' },
  { value: 'none', label: 'Ausgeblendet' },
]

const ALBUM_OVERVIEW_STYLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'rect', label: 'Rechteckig' },
  { value: 'circle', label: 'Rund' },
]

const LAYOUT_MODE_LABELS: Record<string, string> = {
  grid: 'Kacheln', rows: 'Zeilen', columns: 'Spalten', masonry: 'Masonry',
}
const ALBUM_OVERVIEW_LABELS: Record<string, string> = {
  both: 'Desktop & Mobile', desktop: 'Nur Desktop', mobile: 'Nur Mobile', none: 'Ausgeblendet',
}
const ALBUM_OVERVIEW_STYLE_LABELS: Record<string, string> = {
  rect: 'Rechteckig', circle: 'Rund',
}

const KNOWN_SPACING_VALUES = [0, 5, 10, 15, 20]


function ensureHash(color: string): string {
  return color.startsWith('#') ? color : `#${color}`
}

function colorsMatch(a: string, b: string): boolean {
  return a.replace('#', '').toLowerCase() === b.replace('#', '').toLowerCase()
}

function getEffectiveValue<K extends keyof DriveThemeStyleDefinition>(
  driveValue: DriveThemeStyleDefinition[K] | null | undefined,
  theme: DriveThemeStyleDefinition | null,
  field: K,
): DriveThemeStyleDefinition[K] | null {
  if (driveValue !== null && driveValue !== undefined) return driveValue
  return theme ? theme[field] : null
}

type ColorPickerField = 'primary' | 'neutral' | 'content'

const COLOR_FIELD_LABELS: Record<ColorPickerField, string> = {
  primary: 'Primärfarbe',
  neutral: 'Hintergrundfarbe',
  content: 'Textfarbe',
}

export default function ProjectDesignTab({ project, onUpdate }: ProjectDesignTabProps) {
  const { updateDrive } = useClient()
  const tutorialAdvance = useTutorial(s => s.advanceStep)
  const tutorialStep = useTutorial(s => s.activeSeries?.steps[s.currentStepIndex])
  const drive = project.drive

  const currentTheme = ThemeDefinitionsByName[drive.theme_name || 'golden_hour'] ?? null

  const [themeName, setThemeName] = useState(drive.theme_name || 'golden_hour')
  const [colorPrimary, setColorPrimary] = useState(
    ensureHash(getEffectiveValue(drive.color_primary, currentTheme, 'color_primary') || '000000'),
  )
  const [colorNeutral, setColorNeutral] = useState(
    ensureHash(getEffectiveValue(drive.color_neutral, currentTheme, 'color_neutral') || 'ffffff'),
  )
  const [colorContent, setColorContent] = useState(
    ensureHash(getEffectiveValue(drive.color_content, currentTheme, 'color_content') || '000000'),
  )
  // Font state: '' means "use theme default" (or Gambetta/Nunito if no theme)
  const [fontHeader, setFontHeader] = useState(() => {
    if (!drive.font) return ''
    if (currentTheme && drive.font === currentTheme.font_header) return ''
    return drive.font
  })
  const [fontContent, setFontContent] = useState(() => {
    if (!drive.font_content) return ''
    if (currentTheme && drive.font_content === currentTheme.font_content) return ''
    return drive.font_content
  })

  const effectiveSpacing = getEffectiveValue(drive.spacing as DriveThemeStyleDefinition['spacing'], currentTheme, 'spacing')
  const initialSpacing = effectiveSpacing ?? 0
  const isCustomInitial = !KNOWN_SPACING_VALUES.includes(initialSpacing)

  const [spacingSelect, setSpacingSelect] = useState(
    isCustomInitial ? 'custom' : String(initialSpacing),
  )
  const [customSpacing, setCustomSpacing] = useState<number>(initialSpacing)
  const [layoutMode, setLayoutMode] = useState<LayoutMode | ''>(drive.layout_mode || '')
  const [albumOverview, setAlbumOverview] = useState(drive.album_overview || '')
  const [albumOverviewStyle, setAlbumOverviewStyle] = useState(drive.album_overview_style || '')
  const [areElementsContained, setAreElementsContained] = useState<boolean | null>(drive.are_elements_contained)
  const [isSaving, setIsSaving] = useState(false)

  // Color picker modal
  const colorPickerRef = useRef<HTMLDialogElement>(null)
  const [activeColorField, setActiveColorField] = useState<ColorPickerField | null>(null)

  // Map field names to current values and setters
  const colorValues: Record<ColorPickerField, string> = {
    primary: colorPrimary,
    neutral: colorNeutral,
    content: colorContent,
  }
  const colorSetters: Record<ColorPickerField, (val: string) => void> = {
    primary: setColorPrimary,
    neutral: setColorNeutral,
    content: setColorContent,
  }

  // Current selected theme for reset comparison
  const selectedTheme = themeName ? ThemeDefinitionsByName[themeName] ?? null : null
  const effectiveLayoutMode = layoutMode || selectedTheme?.layout_mode || 'grid'
  const isGridLayout = effectiveLayoutMode === 'grid'

  // Check if values deviate from theme defaults
  const currentSpacingValue = spacingSelect === 'custom' ? customSpacing : Number(spacingSelect)
  const themeSpacingDefault = selectedTheme?.spacing ?? 0
  const isLayoutDifferent = selectedTheme && layoutMode !== ''
  const isSpacingDifferent = selectedTheme && currentSpacingValue !== themeSpacingDefault

  // Per-color theme comparison
  const primaryDefault = selectedTheme?.color_primary || '#000000'
  const neutralDefault = selectedTheme?.color_neutral || '#ffffff'
  const contentDefault = selectedTheme?.color_content || '#000000'
  const isPrimaryDifferent = selectedTheme && !colorsMatch(colorPrimary, primaryDefault)
  const isNeutralDifferent = selectedTheme && !colorsMatch(colorNeutral, neutralDefault)
  const isContentDifferent = selectedTheme && !colorsMatch(colorContent, contentDefault)

  // Font theme comparison
  const isFontHeaderDifferent = selectedTheme && fontHeader !== ''
  const isFontContentDifferent = selectedTheme && fontContent !== ''

  // Compute font default labels
  const fontHeaderDefaultLabel = selectedTheme?.font_header
    ? `${getFontDefinitionById(selectedTheme.font_header)?.display ?? selectedTheme.font_header} (Standard)`
    : 'Gambetta (Standard)'
  const fontContentDefaultLabel = selectedTheme?.font_content
    ? `${getFontDefinitionById(selectedTheme.font_content)?.display ?? selectedTheme.font_content} (Standard)`
    : 'Nunito (Standard)'

  // New fields theme comparison
  const isAlbumOverviewDifferent = selectedTheme && albumOverview !== ''
  const isAlbumOverviewStyleDifferent = selectedTheme && albumOverviewStyle !== ''
  const isContainedDifferent = selectedTheme && areElementsContained !== null

  // Compute default labels for dropdowns (show actual value instead of "Theme-Standard")
  const layoutDefaultLabel = selectedTheme?.layout_mode
    ? `${LAYOUT_MODE_LABELS[selectedTheme.layout_mode]} (Standard)`
    : 'Kacheln (Standard)'
  const albumOverviewDefaultLabel = selectedTheme?.album_overview
    ? `${ALBUM_OVERVIEW_LABELS[selectedTheme.album_overview]} (Standard)`
    : 'Desktop & Mobile (Standard)'
  const albumOverviewStyleDefaultLabel = selectedTheme?.album_overview_style
    ? `${ALBUM_OVERVIEW_STYLE_LABELS[selectedTheme.album_overview_style]} (Standard)`
    : 'Rechteckig (Standard)'

  // Preview state
  const [debouncedParams, setDebouncedParams] = useState({
    themeName, colorPrimary, colorNeutral, colorContent,
    spacing: spacingSelect === 'custom' ? customSpacing : Number(spacingSelect),
    layoutMode,
    albumOverview,
    albumOverviewStyle,
    areElementsContained,
    fontHeader,
    fontContent,
  })
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounce form values to preview params
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedParams({
        themeName,
        colorPrimary,
        colorNeutral,
        colorContent,
        spacing: spacingSelect === 'custom' ? customSpacing : Number(spacingSelect),
        layoutMode,
        albumOverview,
        albumOverviewStyle,
        areElementsContained,
        fontHeader,
        fontContent,
      })
    }, 500)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [themeName, colorPrimary, colorNeutral, colorContent, spacingSelect, customSpacing, layoutMode, albumOverview, albumOverviewStyle, areElementsContained, fontHeader, fontContent])

  function buildPreviewUrl(): string {
    const params = new URLSearchParams({
      _preview: '1',
      theme_name: debouncedParams.themeName,
      color_primary: debouncedParams.colorPrimary.replace('#', ''),
      color_neutral: debouncedParams.colorNeutral.replace('#', ''),
      color_content: debouncedParams.colorContent.replace('#', ''),
      spacing: String(debouncedParams.spacing),
    })
    if (debouncedParams.layoutMode) {
      params.set('layout_mode', debouncedParams.layoutMode)
    }
    if (debouncedParams.albumOverview) {
      params.set('album_overview', debouncedParams.albumOverview)
    }
    if (debouncedParams.albumOverviewStyle) {
      params.set('album_overview_style', debouncedParams.albumOverviewStyle)
    }
    if (debouncedParams.areElementsContained !== null) {
      params.set('are_elements_contained', String(debouncedParams.areElementsContained))
    }
    if (debouncedParams.fontHeader) {
      params.set('font', debouncedParams.fontHeader)
    }
    if (debouncedParams.fontContent) {
      params.set('font_content', debouncedParams.fontContent)
    }
    return `${window.location.origin}/d/${drive.id}?${params.toString()}`
  }

  function openColorPicker(field: ColorPickerField) {
    setActiveColorField(field)
    setTimeout(() => colorPickerRef.current?.showModal(), 0)
  }

  function handleThemeChange(newThemeName: string) {
    setThemeName(newThemeName)

    const theme = ThemeDefinitionsByName[newThemeName]
    if (theme) {
      setColorPrimary(theme.color_primary || '#000000')
      setColorNeutral(theme.color_neutral || '#ffffff')
      setColorContent(theme.color_content || '#000000')
      const themeSpacing = theme.spacing ?? 0
      if (KNOWN_SPACING_VALUES.includes(themeSpacing)) {
        setSpacingSelect(String(themeSpacing))
      } else {
        setSpacingSelect('custom')
      }
      setCustomSpacing(themeSpacing)
      setLayoutMode('')
      setAlbumOverview('')
      setAlbumOverviewStyle('')
      setAreElementsContained(null)
      setFontHeader('')
      setFontContent('')
    }

    // Advance tutorial when theme is changed
    if (tutorialStep?.spotId === 'design-theme-select') {
      setTimeout(() => tutorialAdvance(), 0)
    }
  }

  function handleSpacingSelectChange(value: string) {
    setSpacingSelect(value)
    if (value !== 'custom') {
      setCustomSpacing(Number(value))
    }
  }

  function resetLayoutToTheme() {
    setLayoutMode('')
  }

  function resetSpacingToTheme() {
    if (!selectedTheme) return
    const themeSpacing = selectedTheme.spacing ?? 0
    if (KNOWN_SPACING_VALUES.includes(themeSpacing)) {
      setSpacingSelect(String(themeSpacing))
    } else {
      setSpacingSelect('custom')
    }
    setCustomSpacing(themeSpacing)
  }

  async function handleSave() {
    setIsSaving(true)
    try {
      const spacing = spacingSelect === 'custom' ? customSpacing : Number(spacingSelect)
      await updateDrive(drive.id, {
        theme_name: themeName,
        color_primary: colorPrimary.replace('#', ''),
        color_neutral: colorNeutral.replace('#', ''),
        color_content: colorContent.replace('#', ''),
        spacing,
        layout_mode: layoutMode || null,
        album_overview: (albumOverview || null) as DriveThemeStyleDefinition['album_overview'],
        album_overview_style: (albumOverviewStyle || null) as DriveThemeStyleDefinition['album_overview_style'],
        are_elements_contained: areElementsContained,
        font: fontHeader,
        font_content: fontContent,
      })
      onUpdate()
      toast('Design gespeichert', { closeButton: true, autoClose: 2000 })
    } catch {
      toast('Fehler beim Speichern', { closeButton: true, autoClose: 3000, type: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  const previewUrl = buildPreviewUrl()

  return (
    <InspectorPreviewLayout previewUrl={previewUrl}>
      {/* Color picker modal */}
      <Modal ref={colorPickerRef} responsive className="bg-neutral">
        <Modal.Header>
          {activeColorField && COLOR_FIELD_LABELS[activeColorField]}
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
          </form>
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col items-center">
            {activeColorField && (
              <ChromePicker
                disableAlpha
                // @ts-ignore
                color={colorValues[activeColorField]}
                onChange={(c) => colorSetters[activeColorField](c.hex)}
              />
            )}
            <Button className="mt-5" color="primary" onClick={() => colorPickerRef.current?.close()}>
              Fertig
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Save button (at top for visibility) */}
        <TutorialSpot id="design-save-button">
          <Button color="primary" className="w-full" onClick={handleSave} loading={isSaving} disabled={isSaving}>
            Speichern
          </Button>
        </TutorialSpot>

        {/* Theme selection */}
        <section className="bg-neutral p-5">
          <div className="flex items-center gap-2 mb-3">
            <IconLayout size={20} className="text-primary" />
            <h3 className="font-semibold">Design-Vorlage</h3>
          </div>
          <TutorialSpot id="design-theme-select">
            <label className="form-control w-full">
              <div className="label">
                <span className="label-text">Theme</span>
              </div>
              {/* @ts-ignore */}
              <Select
                value={themeName}
                onChange={(e) => handleThemeChange(e.currentTarget.value)}
              >
                {ThemeDefinitions.filter((t) => t.theme_name !== '').map((t) => (
                  <Select.Option key={t.theme_name} value={t.theme_name}>
                    {t.theme_display_name}
                  </Select.Option>
                ))}
              </Select>
            </label>
          </TutorialSpot>
        </section>

        {/* Colors */}
        <section className="bg-neutral p-5">
          <div className="flex items-center gap-2 mb-3">
            <IconPalette size={20} className="text-primary" />
            <h3 className="font-semibold">Farben</h3>
          </div>
          <div className="flex flex-col gap-2">
            <ColorField
              label="Primärfarbe"
              value={colorPrimary}
              onChange={setColorPrimary}
              onRowClick={() => openColorPicker('primary')}
              showReset={!!isPrimaryDifferent}
              onReset={() => setColorPrimary(primaryDefault)}
            />
            <ColorField
              label="Hintergrundfarbe"
              value={colorNeutral}
              onChange={setColorNeutral}
              onRowClick={() => openColorPicker('neutral')}
              showReset={!!isNeutralDifferent}
              onReset={() => setColorNeutral(neutralDefault)}
            />
            <ColorField
              label="Textfarbe"
              value={colorContent}
              onChange={setColorContent}
              onRowClick={() => openColorPicker('content')}
              showReset={!!isContentDifferent}
              onReset={() => setColorContent(contentDefault)}
            />
          </div>
        </section>

        {/* Fonts */}
        <section className="bg-neutral p-5">
          <SectionHeader
            icon={<IconTypography size={20} className="text-primary" />}
            title="Schriftarten"
            showReset={!!(isFontHeaderDifferent || isFontContentDifferent)}
            onReset={() => { setFontHeader(''); setFontContent('') }}
          />
          <div className="flex flex-col gap-3">
            <div className="form-control w-full">
              <div className="label">
                <span className="label-text">Überschrift</span>
              </div>
              <FontPicker
                value={fontHeader}
                onChange={setFontHeader}
                defaultLabel={fontHeaderDefaultLabel}
              />
            </div>
            <div className="form-control w-full">
              <div className="label">
                <span className="label-text">Inhalt</span>
              </div>
              <FontPicker
                value={fontContent}
                onChange={setFontContent}
                defaultLabel={fontContentDefaultLabel}
              />
            </div>
          </div>
        </section>

        {/* Images: Layout + Spacing */}
        <section className="bg-neutral p-5">
          <SectionHeader icon={<IconColumns size={20} className="text-primary" />} title="Bilder"
            showReset={!!(isLayoutDifferent || isSpacingDifferent)}
            onReset={() => { resetLayoutToTheme(); resetSpacingToTheme() }} />
          <div className="flex flex-col gap-3">
            <label className="form-control w-full">
              <div className="label">
                <span className="label-text">Bildanordnung</span>
              </div>
              {/* @ts-ignore */}
              <Select
                value={layoutMode}
                onChange={(e) => setLayoutMode(e.currentTarget.value as LayoutMode | '')}
              >
                <Select.Option value="">{layoutDefaultLabel}</Select.Option>
                {LAYOUT_MODE_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </label>
            {!isGridLayout && (
              <label className="form-control w-full">
                <div className="label">
                  <span className="label-text">Bildabstand</span>
                </div>
                {/* @ts-ignore */}
                <Select
                  value={spacingSelect}
                  onChange={(e) => handleSpacingSelectChange(e.currentTarget.value)}
                >
                  {SPACING_OPTIONS.map((opt) => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
              </label>
            )}
            {!isGridLayout && spacingSelect === 'custom' && (
              <label className="form-control w-full">
                <div className="label">
                  <span className="label-text">Wert (px)</span>
                </div>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={customSpacing}
                  onChange={(e) => setCustomSpacing(Number(e.target.value))}
                />
              </label>
            )}
          </div>
        </section>

        {/* Additional settings */}
        <section className="bg-neutral p-5">
          <SectionHeader icon={<IconSettings size={20} className="text-primary" />} title="Weitere Einstellungen"
            showReset={!!(isAlbumOverviewDifferent || isAlbumOverviewStyleDifferent || isContainedDifferent)}
            onReset={() => { setAlbumOverview(''); setAlbumOverviewStyle(''); setAreElementsContained(null) }} />
          <div className="flex flex-col gap-3">
            <label className="form-control w-full">
              <div className="label">
                <span className="label-text">Albumübersicht</span>
              </div>
              {/* @ts-ignore */}
              <Select
                value={albumOverview}
                onChange={(e) => setAlbumOverview(e.currentTarget.value)}
              >
                <Select.Option value="">{albumOverviewDefaultLabel}</Select.Option>
                {ALBUM_OVERVIEW_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </label>
            <label className="form-control w-full">
              <div className="label">
                <span className="label-text">Albumübersicht-Stil</span>
              </div>
              {/* @ts-ignore */}
              <Select
                value={albumOverviewStyle}
                onChange={(e) => setAlbumOverviewStyle(e.currentTarget.value)}
              >
                <Select.Option value="">{albumOverviewStyleDefaultLabel}</Select.Option>
                {ALBUM_OVERVIEW_STYLE_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </label>
            <div className="flex items-center justify-between">
              <span className="label-text">Volle Breite</span>
              <div className="flex items-center gap-2">
                {areElementsContained !== null && (
                  <button
                    type="button"
                    title="Auf Theme-Standard zurücksetzen"
                    className="text-base-content opacity-40 hover:opacity-100 transition-opacity"
                    onClick={() => setAreElementsContained(null)}
                  >
                    <IconRestore size={16} />
                  </button>
                )}
                <Toggle
                  color="primary"
                  checked={!(areElementsContained ?? selectedTheme?.are_elements_contained ?? true)}
                  onChange={(e) => setAreElementsContained(!e.target.checked)}
                />
              </div>
            </div>
          </div>
        </section>
    </InspectorPreviewLayout>
  )
}

function SectionHeader({ icon, title, showReset, onReset }: {
  icon: React.ReactNode
  title: string
  showReset: boolean
  onReset: () => void
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h3 className="font-semibold flex-1">{title}</h3>
      {showReset && (
        <button
          type="button"
          onClick={onReset}
          title="Auf Theme-Standard zurücksetzen"
          className="text-base-content opacity-40 hover:opacity-100 transition-opacity"
        >
          <IconRestore size={18} />
        </button>
      )}
    </div>
  )
}

function FontPicker({ value, onChange, defaultLabel }: {
  value: string
  onChange: (fontId: string) => void
  defaultLabel: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedFont: FontDefinition | null = value ? getFontDefinitionById(value) ?? null : null

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isOpen])

  function handleSelect(fontId: string) {
    onChange(fontId)
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger – styled like DaisyUI select */}
      <button
        type="button"
        className="select select-bordered w-full text-left flex items-center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span
          className="truncate"
          style={selectedFont ? { fontFamily: selectedFont.css } : undefined}
        >
          {selectedFont ? selectedFont.display : defaultLabel}
        </span>
      </button>

      {/* Dropdown list */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-neutral border border-base-300 shadow-lg max-h-64 overflow-y-auto">
          {/* Default / Standard option */}
          <div
            className={'px-3 py-2 cursor-pointer hover:bg-secondary text-sm ' + (!value ? 'bg-secondary font-semibold' : '')}
            onClick={() => handleSelect('')}
          >
            {defaultLabel}
          </div>
          {/* Font options */}
          {allFonts.map((font) => (
            <div
              key={font.id}
              className={'px-3 py-2 cursor-pointer hover:bg-secondary text-lg ' + (font.id === value ? 'bg-secondary font-semibold' : '')}
              style={{ fontFamily: font.css }}
              onClick={() => handleSelect(font.id)}
            >
              {font.display}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ColorField({
  label,
  value,
  onChange,
  onRowClick,
  showReset,
  onReset,
}: {
  label: string
  value: string
  onChange: (val: string) => void
  onRowClick: () => void
  showReset: boolean
  onReset: () => void
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg px-2 py-1.5 -mx-2">
      {/* Clickable area: circle + label → opens color picker modal */}
      <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={onRowClick}>
        <div
          className="w-9 h-9 rounded-full border-[5px] border-[white] shadow shrink-0"
          style={{ backgroundColor: value }}
        />
        <span className="text-sm">{label}</span>
      </div>
      {/* Fixed-width reset slot - always takes space so input doesn't shift */}
      <div className="w-5 shrink-0 flex items-center justify-center">
        {showReset && (
          <button
            type="button"
            title="Auf Theme-Standard zurücksetzen"
            className="text-base-content opacity-40 hover:opacity-100 transition-opacity"
            onClick={onReset}
          >
            <IconRestore size={16} />
          </button>
        )}
      </div>
      {/* Hex input - always on the right, directly editable */}
      <Input
        size="sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-24 font-mono text-xs"
        maxLength={7}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  )
}
